import React, { useEffect, useState } from 'react';
import { apiService } from '../services/api';
import type { TimelineStats, CountryStats } from '../types';
import {
  ChartBarIcon,
  CheckCircleIcon,
  XCircleIcon,
  QuestionMarkCircleIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

/** Buckets arrive as UTC instants; show them in the reader's own clock. */
const hourLabel = (value: string) =>
  new Date(value).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

const fullLabel = (value: string) =>
  new Date(value).toLocaleString([], {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  });

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<any>({
    total_requests_today: 0,
    allowed_requests: 0,
    denied_requests: 0,
    unknown_requests: 0,
    average_response_time_ms: 0,
    hlr_percentage: 0,
    hss_percentage: 0,
  });
  const [timeline, setTimeline] = useState<TimelineStats[]>([]);
  const [topCountries, setTopCountries] = useState<CountryStats[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [statsData, timelineData, countriesData] = await Promise.all([
        apiService.getStatsOverview(),
        apiService.getStatsTimeline({ hours: 24, interval: 'hour' }),
        apiService.getStatsByCountry({ days: 1 }),
      ]);

      setStats(statsData);
      setTimeline(timelineData || []);
      setTopCountries(countriesData || []);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const pieData = [
    { name: 'HLR', value: stats.hlr_percentage },
    { name: 'HSS', value: stats.hss_percentage },
  ];

  const COLORS = ['#3B82F6', '#10B981'];

  const statCards = [
    {
      name: 'Total Queries Today',
      value: stats.total_requests_today.toLocaleString(),
      icon: ChartBarIcon,
      color: 'bg-blue-500',
    },
    {
      name: 'Allowed',
      value: stats.allowed_requests.toLocaleString(),
      icon: CheckCircleIcon,
      color: 'bg-green-500',
    },
    {
      name: 'Denied',
      value: stats.denied_requests.toLocaleString(),
      icon: XCircleIcon,
      color: 'bg-red-500',
    },
    {
      name: 'Unknown',
      value: stats.unknown_requests.toLocaleString(),
      icon: QuestionMarkCircleIcon,
      color: 'bg-yellow-500',
    },
    {
      name: 'Avg Response Time',
      value: `${stats.average_response_time_ms.toFixed(2)}ms`,
      icon: ClockIcon,
      color: 'bg-purple-500',
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-semibold text-gray-900">Dashboard</h1>

      {/* Stat cards */}
      <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-5">
        {statCards.map((card) => (
          <div
            key={card.name}
            className="bg-white overflow-hidden shadow rounded-lg"
          >
            <div className="p-5">
              <div className="flex items-center">
                <div className={`${card.color} rounded-md p-3`}>
                  <card.icon className="h-6 w-6 text-white" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      {card.name}
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {card.value}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Timeline chart */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">
            Queries by Hour
          </h2>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={timeline}>
                <CartesianGrid strokeDasharray="3 3" />
                {/* The endpoint returns `timestamp` and `count`; this used to
                    ask for `hour` and `total`, so the axis had no labels and
                    the Total line had nothing to draw. */}
                <XAxis dataKey="timestamp" tickFormatter={hourLabel} minTickGap={24} />
                <YAxis />
                <Tooltip labelFormatter={(value) => fullLabel(String(value))} />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="count"
                  stroke="#3B82F6"
                  name="Total"
                  dot={false}
                />
                <Line
                  type="monotone"
                  dataKey="allowed"
                  stroke="#10B981"
                  name="Allowed"
                  dot={false}
                />
                <Line
                  type="monotone"
                  dataKey="denied"
                  stroke="#EF4444"
                  name="Denied"
                  dot={false}
                />
                {/* Denied excludes unknown networks, so without this line the
                    three do not add up to Total. */}
                <Line
                  type="monotone"
                  dataKey="unknown"
                  stroke="#F59E0B"
                  name="Unknown"
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Pie chart */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">
            Query Types
          </h2>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) =>
                    `${name}: ${((percent || 0) * 100).toFixed(0)}%`
                  }
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {pieData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Top countries */}
      <div className="mt-6 bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">
          Top 10 Countries by Registrations
        </h2>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={topCountries}
              layout="horizontal"
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="country" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="registration_count" fill="#3B82F6" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;